import debounce from 'debounce';
import { useEffect, useMemo, useState, useRef } from 'react';
import { ThemeContext } from '@/state/Theme';
import LocatorResults from './LocatorResults';
import GoogleMap from './GoogleMap';
import createMarkerIcon from './createMarkerIcon';
import { type Station } from './Locator.types';

export type LocatorMapProps = {
  currentDuration: string;
  currentStation: string;
  googleMaps: any;
  onSelect: (id?) => void;
  stations: Station[];
};

const LocatorMap: React.FC<LocatorMapProps> = ({
  currentDuration,
  currentStation,
  googleMaps,
  onSelect,
  stations,
}) => {
  // We need to pass the wrapper height to LocatorResults so it can animate the mobile "current station" card.
  const wrapperEl = useRef<HTMLDivElement>();

  // The list of stations to display on the map and in the sidebar.
  // This list should only include stations which are visible on the map.
  const [stationsInViewport, setStationsInViewport] = useState([]);

  // Google maps markers rendered on the map.
  const [renderedMarkers, setRenderedMarkers] = useState<Array<any> | object>(
    [],
  );

  // When there are no stations, for example when the user searches for a station that doesn't exist, clear the sub set
  // of stations displayed in the results list because handle bounds change will not trigger.
  useEffect(() => {
    if (stations.length < 1) {
      setStationsInViewport([]);
    } else {
      setStationsInViewport(stations);
    }
  }, [stations]);

  // When the map bounds change filter the stations list to the stations inside the map viewport.
  const handleBoundsChanged = useMemo(
    () =>
      debounce(({ map, renderedMarkers }) => {
        try {
          const mapBounds = map.getBounds();
          const markersInViewport = renderedMarkers.filter((marker) =>
            mapBounds.contains(marker.getPosition()),
          );

          const newStationsInViewport = stations.filter(
            ({ location }) =>
              markersInViewport.find((m) => {
                const { lat, lng } = m.getPosition();
                return (
                  Number(location.latitude) === lat() &&
                  Number(location.longitude) === lng()
                );
              }) !== undefined,
          );

          setStationsInViewport(newStationsInViewport);
        } catch (err) {
          //
        }
      }, 300),
    [stations],
  );

  // When the user selects a marker, make that the `currentStation`.
  const handleMarkerClick = (uuid) => onSelect(uuid);

  // Clear the `currentStation` when the users taps the close button (mobile only).
  const handleCurrentStationClose = () => onSelect();

  const markerData = useMemo(() => {
    let markers = [];

    if (stations && googleMaps) {
      markers = stations
        .filter(({ location }) => location.latitude && location.longitude)
        .map(({ location, uuid, type }) => ({
          id: uuid,
          position: {
            lat: Number(location.latitude),
            lng: Number(location.longitude),
          },
          listeners: { click: handleMarkerClick },
          icon: createMarkerIcon(type, googleMaps),
        }));
    }

    return markers;
  }, [stations, googleMaps]);

  // Update marker sizes when the user selects a station from the results list or from the map.
  useEffect(() => {
    if (Array.isArray(renderedMarkers)) {
      renderedMarkers.forEach((m) => {
        const icon = m.getIcon();

        m.setIcon({
          ...icon,
          size: new googleMaps.Size(34, 45),
          scaledSize: new googleMaps.Size(34, 45),
          anchor: new googleMaps.Point(17, 45),
        });
      });
    }

    const currentMarkerData = markerData.find(
      ({ id }) => id === currentStation,
    );

    if (!currentMarkerData) {
      return;
    }

    if (Array.isArray(renderedMarkers)) {
      const currentRenderedMarker = renderedMarkers.find((m) => {
        const { lat, lng } = m.getPosition();
        return (
          lat() === currentMarkerData.position.lat &&
          lng() === currentMarkerData.position.lng
        );
      });

      if (currentRenderedMarker) {
        const icon = currentRenderedMarker.getIcon();

        currentRenderedMarker.setIcon({
          ...icon,
          size: new googleMaps.Size(48, 63),
          scaledSize: new googleMaps.Size(48, 63),
          anchor: new googleMaps.Point(24, 63),
        });
      }
    }
  }, [currentStation, googleMaps]);

  // Only rerender the map when marker data changes.
  const MapComponent = useMemo(
    () => (
      <ThemeContext.Consumer>
        {({ brandColor }) => (
          <GoogleMap
            api={googleMaps}
            classNames="locator-map__map"
            markers={markerData}
            fitMarkerBounds
            clusterOptions={{
              renderer: {
                render: ({ count, position }) => {
                  const icon = `
                  <svg fill="${brandColor}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                    <circle cx="120" cy="120" stroke="${brandColor}" stroke-width="8" fill-opacity="60%" r="70" />
                  </svg>
                `;
                  const svg = window.btoa(icon);

                  return new googleMaps.Marker({
                    position,
                    icon: {
                      url: `data:image/svg+xml;base64,${svg}`,
                      scaledSize: new googleMaps.Size(75, 75),
                      origin: new googleMaps.Point(0, 0),
                      anchor: new googleMaps.Point(37, 37),
                    },
                    label: {
                      text: String(count),
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    },
                    zIndex: Number(googleMaps.Marker.MAX_ZINDEX) + count,
                  });
                },
              },
            }}
            listeners={{ bounds_changed: handleBoundsChanged }}
            options={{ zoom: 6 }}
            onUpdateMarkers={(m) => setRenderedMarkers(m)}
          />
        )}
      </ThemeContext.Consumer>
    ),
    [googleMaps, markerData],
  );

  return (
    <div className="locator-map" ref={wrapperEl}>
      <div className="locator-map__container container">
        <LocatorResults
          currentDuration={currentDuration}
          currentStation={currentStation}
          stations={stationsInViewport}
          onCurrentStationClose={handleCurrentStationClose}
          wrapperHeight={wrapperEl.current ? wrapperEl.current.clientHeight : 0}
        />
        {MapComponent}
      </div>
    </div>
  );
};

export default LocatorMap;
